// @perlab/cross-tab — CrossTabBus interface + BroadcastChannelBus + NoopBus
// + fail-closed createBus() factory.
//
// Per spec §3.2 (28_Parallel_Work_Architecture_Tier1_V3.md):
//   §3.2.1 — envelope + HLC (see envelope.ts)
//   §3.2.2 — BroadcastChannelBus is default; StorageEventBus is Slice 2
//   §3.2.3 — fail-closed: missing tenantId/userId outside test → NoopBus
//   §3.2.4 — echo guard: applyRemote sets suppressBroadcastRef before local
//            apply; setter checks ref before re-broadcasting
//   §3.2.7 — __inspect() returns sub-count map + recent-50 message ring
//
// Out of scope for this slice (Slice 2):
//   - StorageEventBus fallback
//   - leader-election integration
//   - <BusDevtools/> React surface

import {
  type BusEnvelope,
  nextHlc,
  observeRemoteHlc,
} from './envelope.js';

const CHANNEL_NAME = 'perlab.shell.bus';
const RECENT_RING_SIZE = 50;

export interface BusInit {
  /** REQUIRED — no default. Resolves HIGH N4 + N7 (§3.2.3). */
  tenantId: string;
  /** REQUIRED — no default. */
  userId: string;
  /** Override tabId (test only). Production calls auto-generate. */
  tabId?: string;
}

export interface BusInspectSnapshot {
  /** type → live handler count. */
  subscriptions: Record<string, number>;
  /** Last N envelopes observed (publish OR receive); newest last. */
  recentMessages: BusEnvelope[];
}

export interface CrossTabBus {
  readonly tabId: string;
  readonly tenantId: string;
  readonly userId: string;

  /**
   * Publish a typed message to all other tabs. Injects HLC + tenant + user
   * from the bus init — consumers cannot forget or override these (§3.2.3).
   */
  publish<T>(type: string, payload: T): void;

  /**
   * Subscribe to a message type. Returns an unsubscribe function.
   * Handler is invoked AFTER tenant/user guard + AFTER own-tab echo skip.
   */
  subscribe<T>(type: string, handler: (env: BusEnvelope<T>) => void): () => void;

  /**
   * Used by snapshot consumers (e.g. useTheme) before publishing an outbound
   * update derived from an inbound applyRemote. While the returned token is
   * "active" (the publish runs synchronously after applyRemote), the bus
   * skips broadcasting for the matching type. Released via queueMicrotask
   * per §3.2.4.
   */
  suppressBroadcastForType(type: string): void;

  /** Devtools / test hook — see BusInspectSnapshot. */
  __inspect(): BusInspectSnapshot;

  /** Test hook — inject an envelope as if received from another tab. */
  __inject<T>(env: BusEnvelope<T>): void;

  /** Tear down underlying transport. Idempotent. */
  close(): void;
}

/**
 * Best-effort tab id. Uses crypto.randomUUID where available; falls back to
 * a Math.random hex string in older test envs. Not security-sensitive —
 * only needs uniqueness across concurrently open tabs.
 */
function generateTabId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `tab-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

/**
 * Abstract base — holds shared state (recent ring, subscription map, echo
 * guard, tenant/user) and concrete transports plug in via abstract methods.
 */
abstract class BaseBus implements CrossTabBus {
  readonly tabId: string;
  readonly tenantId: string;
  readonly userId: string;

  private handlers = new Map<string, Set<(env: BusEnvelope<unknown>) => void>>();
  private recent: BusEnvelope[] = [];
  // Echo guard per §3.2.4 — per-type suppression flag. Set true synchronously
  // by applyRemote consumers; cleared next microtask. While true, publish of
  // the same type is dropped (we just received this from a peer; do NOT
  // re-broadcast it).
  private suppress = new Set<string>();

  constructor(init: BusInit) {
    this.tabId = init.tabId ?? generateTabId();
    this.tenantId = init.tenantId;
    this.userId = init.userId;
  }

  // --- Transport-specific ---
  protected abstract postOut(env: BusEnvelope): void;
  abstract close(): void;

  // --- Receive entry point (called by transport on inbound envelope) ---
  protected receive(env: BusEnvelope): void {
    // Tenant/user guard — fail-closed per §3.2.3. Drop silently before any
    // handler sees it; this prevents cross-tenant state bleeding.
    if (env.tenantId !== this.tenantId || env.userId !== this.userId) return;
    // Own-tab echo skip — receivers ignore their own publishes.
    if (env.tabId === this.tabId) return;
    // HLC merge per §3.2.1 — must happen BEFORE handler dispatch so any
    // outbound publish made inside a handler uses the merged HLC.
    observeRemoteHlc(env.hlc);
    this.pushRecent(env);
    const set = this.handlers.get(env.type);
    if (!set || set.size === 0) return;
    // Snapshot to array so a handler unsubscribing mid-iteration is safe.
    for (const h of Array.from(set)) {
      try {
        h(env);
      } catch (err) {
        // Per spec §3.2 — a single bad handler must not poison delivery to
        // siblings. Log + continue.
        // eslint-disable-next-line no-console
        console.error(`[cross-tab] handler for '${env.type}' threw`, err);
      }
    }
  }

  publish<T>(type: string, payload: T): void {
    // Echo guard — if this publish was triggered inside an applyRemote of
    // the SAME type, drop it. Caller marks the type via suppressBroadcastForType.
    if (this.suppress.has(type)) return;
    const env: BusEnvelope<T> = {
      type,
      tabId: this.tabId,
      hlc: nextHlc(),
      ts: Date.now(),
      tenantId: this.tenantId,
      userId: this.userId,
      payload,
    };
    this.pushRecent(env);
    this.postOut(env);
  }

  subscribe<T>(type: string, handler: (env: BusEnvelope<T>) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const wrapped = handler as (env: BusEnvelope<unknown>) => void;
    set.add(wrapped);
    return () => {
      const cur = this.handlers.get(type);
      if (!cur) return;
      cur.delete(wrapped);
      if (cur.size === 0) this.handlers.delete(type);
    };
  }

  suppressBroadcastForType(type: string): void {
    this.suppress.add(type);
    // Release in next microtask — §3.2.4 explicit.
    queueMicrotask(() => this.suppress.delete(type));
  }

  __inspect(): BusInspectSnapshot {
    const subscriptions: Record<string, number> = {};
    for (const [t, set] of this.handlers) subscriptions[t] = set.size;
    return {
      subscriptions,
      recentMessages: this.recent.slice(),
    };
  }

  __inject<T>(env: BusEnvelope<T>): void {
    this.receive(env as BusEnvelope);
  }

  private pushRecent(env: BusEnvelope): void {
    this.recent.push(env);
    if (this.recent.length > RECENT_RING_SIZE) {
      this.recent.splice(0, this.recent.length - RECENT_RING_SIZE);
    }
  }
}

/**
 * Default transport — uses the BroadcastChannel API. Available in all modern
 * browsers (Chrome/Edge/Firefox/Safari 15.4+). Falls back to NoopBus via the
 * factory when unavailable.
 */
export class BroadcastChannelBus extends BaseBus {
  private channel: BroadcastChannel;
  private closed = false;

  constructor(init: BusInit) {
    super(init);
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.addEventListener('message', this.onMessage);
  }

  private onMessage = (ev: MessageEvent): void => {
    const env = ev.data as BusEnvelope | undefined;
    if (!env || typeof env !== 'object' || typeof env.type !== 'string') return;
    this.receive(env);
  };

  protected postOut(env: BusEnvelope): void {
    // A publish can land AFTER close() — React StrictMode remounts the provider
    // (close old bus → publish from an async-fired setter → new bus), a tab is
    // tearing down, or a queued setter fires late. postMessage on a closed
    // BroadcastChannel throws InvalidStateError; uncaught, that white-screens the
    // whole shell. The channel is gone and there are no peers to receive, so
    // dropping the envelope is the correct fail-safe (bus fail-closed, §3.2.3).
    if (this.closed) return;
    // BroadcastChannel.postMessage clones structured-cloneable values.
    this.channel.postMessage(env);
  }

  close(): void {
    this.closed = true;
    this.channel.removeEventListener('message', this.onMessage);
    this.channel.close();
  }
}

/**
 * No-op transport. Used when tenant/user auth is unresolved, or when both
 * real transports are unavailable. Every consumer still operates correctly
 * on local state — the bus is decorative.
 */
export class NoopBus extends BaseBus {
  constructor(init: BusInit) {
    super(init);
  }
  protected postOut(_env: BusEnvelope): void {
    // Intentionally empty — no peers to notify.
  }
  close(): void {
    // No resources to release.
  }
}

/**
 * Per §3.2.3 — fail-closed factory. Missing tenantId/userId outside a test
 * env yields a NoopBus + error log; never throws (would break app boot if
 * auth races bus construction).
 *
 * Test env (NODE_ENV === 'test') is allowed to pass {_test_, _test_} sentinel
 * values automatically — this matches the V8 spec test that DOES throw on
 * '_dev_' defaults but tolerates explicit test usage.
 */
export function createBus(init: BusInit): CrossTabBus {
  const isTest = getNodeEnv() === 'test';
  // V8 guard — '_dev_' as tenantId is forbidden outside test env.
  if (!isTest && init.tenantId === '_dev_') {
    throw new Error('[cross-tab] dev defaults forbidden outside test');
  }
  if (!init.tenantId || !init.userId) {
    if (!isTest) {
      // eslint-disable-next-line no-console
      console.error('[cross-tab] auth not resolved; using NoopBus');
      return new NoopBus({ tenantId: '_unresolved_', userId: '_unresolved_', tabId: init.tabId });
    }
    // Test env: tag as test
    return new NoopBus({ tenantId: '_test_', userId: '_test_', tabId: init.tabId });
  }
  if (typeof BroadcastChannel === 'undefined') {
    // StorageEventBus fallback is Slice 2. For Slice 1, degrade to NoopBus.
    // eslint-disable-next-line no-console
    console.warn('[cross-tab] BroadcastChannel unavailable; using NoopBus until StorageEventBus lands (Slice 2)');
    return new NoopBus(init);
  }
  return new BroadcastChannelBus(init);
}

/**
 * Robust NODE_ENV read. process may not exist in browser builds without
 * polyfill — guard accordingly.
 */
function getNodeEnv(): string | undefined {
  const p = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  return p?.env?.NODE_ENV;
}
